import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PageLayout from '../components/PageLayout';
import PageBanner from '../components/PageBanner';
import SEO from '../components/SEO';
import API from '../api';
import { useLanguage } from '../context/LanguageContext';

// Manufacturer directory, modeled on the corporate site's "取扱メーカー"
// pages. Each name links to that brand's own categories (BrandCatalog.jsx)
// rather than to products. Three ways to find a brand — by product line,
// by first letter, or by typing a name.
const VIEWS = ['category', 'alpha', 'name'];

function firstLetter(name) {
  const c = (name || '').trim().charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : /[0-9]/.test(c) ? '0-9' : '#';
}

function BrandTable({ items }) {
  return (
    // Every cell draws a left border; the wrapper clips the first column's
    // so only the dividers between columns remain, at any column count.
    <div className="overflow-hidden border-t border-gray-300">
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 -ml-px">
        {items.map(b => (
          <li key={b.id} className="border-b border-l border-gray-300">
            <Link to={`/brands/${b.slug}`}
              className="group flex items-center justify-between gap-3 px-6 py-5 text-sm sm:text-base text-charcoal hover:text-primary hover:bg-[#f5f5f5] transition-colors">
              {b.name}
              <span className="w-1.5 h-1.5 shrink-0 border-t-2 border-r-2 border-gray-300 rotate-45 group-hover:border-primary transition-colors" />
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Pill({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className={`rounded-full border px-5 py-2 text-sm transition-colors ${active
        ? 'bg-primary border-primary text-white'
        : 'border-gray-300 text-charcoal hover:border-primary hover:text-primary'}`}>
      {children}
    </button>
  );
}

export default function Brands() {
  const { t } = useLanguage();
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('category');
  const [activeGroup, setActiveGroup] = useState(null); // category key or letter; null = all
  const [query, setQuery] = useState('');

  useEffect(() => {
    API.get('/brands').then(r => setBrands(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  // Already ordered by category sort_order then brand sort_order (API side) —
  // group into sections here, uncategorized brands trailing under "Other".
  const categoryGroups = useMemo(() => {
    const groups = [];
    const byKey = new Map();
    brands.forEach(b => {
      const key = String(b.category_id || 'other');
      let group = byKey.get(key);
      if (!group) {
        group = { key, name: b.category_name || t('brands.other'), items: [] };
        byKey.set(key, group);
        groups.push(group);
      }
      group.items.push(b);
    });
    return groups;
  }, [brands, t]);

  const letterGroups = useMemo(() => {
    const byKey = new Map();
    [...brands].sort((a, b) => a.name.localeCompare(b.name)).forEach(b => {
      const key = firstLetter(b.name);
      if (!byKey.has(key)) byKey.set(key, { key, name: key, items: [] });
      byKey.get(key).items.push(b);
    });
    return [...byKey.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [brands]);

  const searchResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return brands.filter(b => b.name.toLowerCase().includes(q)).sort((a, b) => a.name.localeCompare(b.name));
  }, [brands, query]);

  const switchView = (v) => { setView(v); setActiveGroup(null); };

  const groups = view === 'category' ? categoryGroups : letterGroups;
  const shownGroups = activeGroup ? groups.filter(g => g.key === activeGroup) : groups;

  return (
    <PageLayout>
      <SEO title={t('common.brands')} description="The brands Sankyo Machine Tools carries in the Philippines, organized by product line." />
      <PageBanner en="OUR BRANDS" title={t('brands.title')} breadcrumbs={[{ label: t('common.brands') }]} />

      <section className="pt-6 pb-20 lg:pb-28 bg-white">
        <div className="w-[90%] max-w-7xl mx-auto">
          {/* View switcher */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-8">
            {VIEWS.map(v => (
              <button key={v} onClick={() => switchView(v)} aria-pressed={view === v}
                className={`min-h-[56px] lg:min-h-[62px] px-4 font-bold text-sm sm:text-base transition-colors ${view === v
                  ? 'bg-primary text-white'
                  : 'bg-[#d7d7d7] text-charcoal hover:bg-primary hover:text-white'}`}>
                {t(v === 'category' ? 'brands.byCategory' : v === 'alpha' ? 'brands.byAlpha' : 'brands.byName')}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => <div key={i} className="h-14 bg-gray-100 animate-pulse" />)}
            </div>
          ) : brands.length === 0 ? (
            <p className="text-center text-gray-400 text-lg py-20">{t('brands.empty')}</p>
          ) : view === 'name' ? (
            <div>
              <label htmlFor="brand-search" className="block font-bold text-charcoal mb-3">{t('brands.searchLabel')}</label>
              <input id="brand-search" type="search" value={query} onChange={e => setQuery(e.target.value)}
                placeholder={t('brands.searchPlaceholder')} autoFocus
                className="w-full max-w-xl border border-gray-300 px-5 py-3.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary mb-10" />
              {query.trim() && (searchResults.length > 0
                ? <BrandTable items={searchResults} />
                : <p className="text-gray-400 py-10">{t('brands.noResults')}</p>)}
            </div>
          ) : (
            <>
              {/* Group filter pills */}
              <div className="flex flex-wrap gap-2.5 mb-14">
                {groups.map(g => (
                  <Pill key={g.key} active={activeGroup === g.key} onClick={() => setActiveGroup(activeGroup === g.key ? null : g.key)}>
                    {g.name}
                  </Pill>
                ))}
              </div>

              <div className="space-y-16">
                {shownGroups.map(g => (
                  <section key={g.key}>
                    <h2 className="text-3xl sm:text-4xl lg:text-[2.75rem] font-bold text-ink mb-8 leading-tight">{g.name}</h2>
                    <BrandTable items={g.items} />
                  </section>
                ))}
              </div>
            </>
          )}
        </div>
      </section>
    </PageLayout>
  );
}
